import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import Button from '../../shared/TailwindComponents/Button';
import { completeTask } from '../../../store/actions/TasksActions';
import { completeDashboardTask, fetchDashboardSummary } from '../../../store/actions/DashboardActions';
import { DashboardUrgentTask, DashboardCollaborator } from '../../../store/reducer/DashboardReducer';
import Badge from '../../shared/TailwindComponents/Badge';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import ProfileImage from '../../Header/ProfileImage';
import { formatDateTime } from '../../../utils/functions';
import {
  PiCheckSquareOffsetLight,
  PiArrowRightLight,
  PiCalendarLight,
  PiCheckLight,
  PiPlusLight,
} from 'react-icons/pi';

interface RecentTasksProps {
  tasks: DashboardUrgentTask[];
  loading?: boolean;
}

const getDueDateInfo = (dueDate: string | null): { text: string; className: string } => {
  if (!dueDate) return { text: '', className: 'text-gray-400' };
  const due = new Date(dueDate);
  const now = new Date();
  const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return {
    text: `${Math.abs(diffDays)}d overdue`,
    className: 'text-red-600 dark:text-red-400',
  };
  if (diffDays === 0) return { text: 'Due today', className: 'text-amber-600 dark:text-amber-400' };
  if (diffDays === 1) return { text: 'Due tomorrow', className: 'text-amber-500 dark:text-amber-400' };
  if (diffDays <= 7) return { text: `Due in ${diffDays}d`, className: 'text-amber-500 dark:text-amber-400' };
  return { text: formatDateTime(dueDate), className: 'text-gray-500 dark:text-gray-400' };
};

const getPriorityColor = (priority: string): BadgeColor => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'red';
    case 'high':     return 'orange';
    case 'medium':   return 'yellow';
    default:         return 'gray';
  }
};

const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const AvatarStack: React.FC<{ collaborators: DashboardCollaborator[]; total: number }> = ({
  collaborators,
  total,
}) => {
  const shown = collaborators.slice(0, 4);
  const overflow = total - shown.length;
  if (shown.length === 0) return null;
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((c, i) => (
        <div
          key={i}
          className="border-2 border-white dark:border-gray-800 rounded-full flex-shrink-0"
          title={c.name}
        >
          <ProfileImage
            profilePicture={c.profile_picture || null}
            email={c.email || ''}
            initials={c.initials || '??'}
            name={c.name || ''}
            size="w-6 h-6"
          />
        </div>
      ))}
      {overflow > 0 && (
        <div className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800
          bg-gray-100 dark:bg-gray-700 flex items-center justify-center
          text-[9px] text-gray-500 dark:text-gray-400">
          +{overflow}
        </div>
      )}
    </div>
  );
};

const TaskCard: React.FC<{
  task: DashboardUrgentTask;
  onClick: () => void;
  onComplete: (e: React.MouseEvent) => void;
}> = ({ task, onClick, onComplete }) => {
  const { t } = useTranslation();
  const dueInfo = getDueDateInfo(task.due_date);

  return (
    <div
      onClick={onClick}
      className="group/card relative flex-shrink-0 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80
        p-4 cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600
        transition-all duration-200 flex flex-col gap-3 snap-start"
    >
      <div className="flex items-start gap-2.5">
        <button
          onClick={onComplete}
          className="group/btn flex-shrink-0 w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600
            group-hover/card:border-green-400 group-hover/card:bg-green-50
            dark:group-hover/card:border-green-500 dark:group-hover/card:bg-green-900/20
            flex items-center justify-center transition-all mt-0.5
            hover:!border-green-500 hover:!bg-green-100 dark:hover:!bg-green-900/40 hover:scale-110"
          title={t('Complete task')}
        >
          <PiCheckLight className="w-3 h-3 text-gray-400 group-hover/btn:text-green-500 transition-colors" />
        </button>
        <h3 className="flex-1 text-sm text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">
          {task.title}
        </h3>
        <PiArrowRightLight className="flex-shrink-0 w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover/card:opacity-100 transition-opacity mt-0.5" />
      </div>

      {dueInfo.text && (
        <div className={`flex items-center gap-1 text-xs ${dueInfo.className}`}>
          <PiCalendarLight className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{dueInfo.text}</span>
        </div>
      )}

      <div className="mt-auto flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700/60">
        <div className="flex items-center gap-2 min-w-0">
          <AvatarStack collaborators={task.collaborators ?? []} total={task.collaborator_count ?? 0} />
          {task.matter_title && (
            <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[100px]">
              {task.matter_title}
            </span>
          )}
        </div>
        <Badge color={getPriorityColor(task.priority)}>
          {capitalize(task.priority)}
        </Badge>
      </div>
    </div>
  );
};

const NewTaskCard: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClick}
      className="group flex-shrink-0 w-72 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700
        cursor-pointer snap-start min-h-[140px]
        hover:border-violet-300 dark:hover:border-violet-600
        hover:bg-violet-50/40 dark:hover:bg-violet-900/10
        transition-all duration-200
        flex flex-col items-center justify-center gap-2"
    >
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full
        bg-gray-100 dark:bg-gray-700
        group-hover:bg-violet-100 dark:group-hover:bg-violet-900/30
        transition-colors">
        <PiPlusLight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors" />
      </div>
      <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
        {t('New Task')}
      </span>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="flex-shrink-0 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 p-4 flex flex-col gap-3 snap-start">
    <div className="flex items-start gap-2.5">
      <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0 animate-fadePulse" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse w-4/5" />
        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse w-3/5" />
      </div>
    </div>
    <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
    <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700/60">
      <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
      <div className="h-5 w-14 bg-gray-200 dark:bg-gray-700 rounded-full animate-fadePulse" />
    </div>
  </div>
);

const RecentTasks: React.FC<RecentTasksProps> = ({ tasks, loading }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { hasSectionAccess } = useLicenseGate();

  if (!hasSectionAccess('tasks')) return null;

  const capped = tasks.slice(0, 8);

  const handleComplete = async (e: React.MouseEvent, taskUuid: string) => {
    e.stopPropagation();
    dispatch(completeDashboardTask(taskUuid));
    await dispatch(completeTask(taskUuid) as any);
    dispatch(fetchDashboardSummary() as any);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2.5">
          <PiCheckSquareOffsetLight className="w-4 h-4 text-gray-800 dark:text-gray-300" />
          <h3 className="text-sm text-gray-900 dark:text-gray-100">
            {t('Upcoming Tasks')}
          </h3>
          {capped.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
              {capped.length}
            </span>
          )}
        </div>
        <Button variant="outline" size="small" onClick={() => history.push('/tasks')}>
          {t('View All')}
        </Button>
      </div>

      <div className="px-5 pt-4 bg-gray-50 dark:bg-gray-900/30">
        {loading ? (
          <div className="flex gap-3 overflow-x-auto custom-scrollbar-main pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
            {[0, 1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : capped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-50 dark:bg-violet-900/20 mb-3">
              <PiCheckSquareOffsetLight className="w-6 h-6 text-violet-400 dark:text-violet-500" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('No upcoming tasks')}
            </p>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto custom-scrollbar-main pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
            {capped.map((task) => (
              <TaskCard
                key={task.uuid}
                task={task}
                onClick={() => history.push(`/tasks/${task.uuid}`)}
                onComplete={(e) => handleComplete(e, task.uuid)}
              />
            ))}
            <NewTaskCard onClick={() => history.push('/tasks?add=true')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentTasks;
